import { types, getParent, flow } from 'mobx-state-tree';
import { when } from 'mobx';

const BASE_URL = 'account/v1/source-accounts';

const SourceAccount = types.model('SourceAccount', {
  currency: types.string,
  id: types.string,
  marketplaceId: types.string,
  marketplaceTypeId: types.string,
  name: types.string,
  isBuyItForMeAccount: types.boolean,
  isActive: types.boolean,
  otpSecretKey: types.maybe(types.string),
  password: types.maybe(types.string),
  lastDigitsCard: types.maybe(types.string),
  dateCreated: types.maybe(types.string),
});

const ConnectSource = types
  .model('ConnectSource', {
    accounts: types.optional(types.array(SourceAccount), []),
    sourceMarketplace: types.optional(types.union(types.number, types.string), ''),
    accountsLoaded: types.optional(types.boolean, false),
    username: types.optional(types.string, ''),
    password: types.optional(types.string, ''),
  })
  .views(self => ({
    get sources() {
      return getParent(self, 2).markets.sources;
    },
    get selectedSourceName() {
      const source = self.sources.find(({ id }) => id === self.sourceMarketplace);
      return source ? source.name : '';
    },
    get showToast() {
      return getParent(self, 3).toastsStore.showToast;
    },
    get enums() {
      return getParent(self, 3).enumsStore.getEnumsList;
    },
  }))
  .actions(self => ({
    afterCreate() {
      when(
        () => getParent(self, 2).markets.defaultSource !== null,
        () => self.setSource(getParent(self, 2).markets.defaultSource.id),
      );
    },
    setUsername(e) {
      self.username = e.target.value.trim();
    },
    setPassword(e) {
      self.password = e.target.value;
    },
    setSource(value) {
      self.sourceMarketplace = value;
    },
    fetchAccounts: flow(function* fetchAccounts(currency) {
      try {
        const URL = `${BASE_URL}/non-bifm?currency=${currency}`;
        const { data } = yield getParent(self).transport5007.get(URL, {}, {});
        self.accounts = data;
        self.accountsLoaded = true;
      } catch (err) {
        console.error(err);
      }
    }),
    connectSourceAccount: flow(function* connectSourceAccount(data) {
      try {
        const URL = `${BASE_URL}/create-source-account-v2`;
        yield getParent(self).transport5007.post(URL, data, {}, {});
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: "You've successfully connected your source account.",
        });
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error while connecting your source account. Please try again.',
        });
      } finally {
        yield getParent(self).fetchConnectedAccounts();
      }
    }),
    loginAccount: flow(function* loginAccount(id) {
      try {
        const URL = `${BASE_URL}/login-source-account-v2/${id}`;
        yield getParent(self).transport5007.put(URL, {}, {}, {});
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: "You've successfully connected your source account.",
        });
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error while connecting your source account. Please try again.',
        });
      } finally {
        yield getParent(self).fetchConnectedAccounts();
      }
    }),
    logoutAccount: flow(function* logoutAccount(id) {
      try {
        const URL = `${BASE_URL}/logout-source-account-v2/${id}`;
        yield getParent(self).transport5007.put(URL, {}, {});
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: "You've successfully disconnected your source account.",
        });
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error while disconnecting your source account. Please try again.',
        });
      } finally {
        yield getParent(self).fetchConnectedAccounts();
      }
    }),
    editSourceAccount: flow(function* editSourceAccount(data, id) {
      try {
        const URL = `${BASE_URL}/update-source-account-v2/${id}`;
        yield getParent(self).transport5007.put(URL, data, {}, {});
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: "You've successfully updated your source account.",
        });
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error while updating your source account. Please try again.',
        });
      } finally {
        yield getParent(self).fetchConnectedAccounts();
      }
    }),
    deleteSourceAccount: flow(function* deleteSourceAccount(id) {
      try {
        const URL = `${BASE_URL}/delete-source-account-v2/${id}`;
        yield getParent(self).transport5007.delete(URL, {}, {});
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: "You've successfully deleted your source account.",
        });
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error while deleting your source account. Please try again.',
        });
      } finally {
        yield getParent(self).fetchConnectedAccounts();
      }
    }),
    connectSourceAccountAndUpdateList: flow(function* connectSourceAccount() {
      try {
        const data = {
          marketplaceId: self.sourceMarketplace,
          name: self.username,
          password: self.password,
        };
        yield getParent(self).transport5007.post(URL, data, {}, {});
        self.showToast({
          type: self.enums.TOAST_TYPES.INFO,
          message: 'Source marketplace credentials saved successful',
        });
      } catch (err) {
        console.error(err);
        self.showToast({
          type: self.enums.TOAST_TYPES.WARNING,
          message: 'There was an error adding the account',
        });
      } finally {
        yield getParent(self).fetchConnectedAccounts();
      }
    }),
    resetFields() {
      self.password = '';
      self.username = '';
      self.sourceMarketplace = getParent(self, 2).markets.defaultSource.id;
    },
  }));

export default ConnectSource;
